import type { AppLocale } from '../i18n';

export type LandingLang = AppLocale;

export const LANDING_LANG_OPTIONS: {
    id: LandingLang;
    /** Short label in header */
    label: string;
    /** Native name in menu */
    nativeLabel: string;
}[] = [
    { id: 'en', label: 'EN', nativeLabel: 'English' },
    { id: 'es', label: 'ES', nativeLabel: 'Español' },
    { id: 'zh', label: '中文', nativeLabel: '简体中文' },
];

export type LandingCopy = {
    htmlLang: string;
    homeAria: string;
    navAria: string;
    navProduct: string;
    navPricing: string;
    navSecurity: string;
    signIn: string;
    signUp: string;
    openApp: string;
    closeMenu: string;
    openMenu: string;
    langMenuAria: string;
    heroTitleBefore: string;
    heroTitleHighlight: string;
    /** Punctuation after highlight (e.g. ? or ？) */
    heroTitleAfterHighlight: string;
    heroSub1: string;
    heroSub2: string;
    tryQuickMask: string;
    demoVideoAria: string;
    demoLine1: string;
    demoLine2: string;
    productEyebrow: string;
    productHeading: string;
    productSub: string;
    detectionSr: string;
    detectionLabels: [string, string, string, string];
    steps: [{ title: string; body: string }, { title: string; body: string }, { title: string; body: string }];
    seeDemo: string;
    pricingHeading: string;
    pricingIntro: string;
    pricingFreeBold: string;
    pricingFreeRest: string;
    pricingPaidBold: string;
    pricingPaidRest: string;
    legalTermsLead: string;
    /** Between privacy and terms links in pricing legal line */
    pricingBetweenLegalLinks: string;
    /** After terms link, before “more questions” */
    pricingAfterLegalBlock: string;
    privacyPolicy: string;
    termsOfService: string;
    moreQuestionsFaq: string;
    createFreeAccount: string;
    /** After FAQ link on pricing legal line */
    pricingLegalClosing: string;
    securityHeading: string;
    security1Title: string;
    security1Body: string;
    security2Title: string;
    security2Body: string;
    security3Title: string;
    security3Body: string;
    securityP3Before: string;
    securityP3After: string;
    securityP4: string;
    faqHeading: string;
    faq1q: string;
    faq1a: string;
    faq2q: string;
    faq2aBeforeTerms: string;
    faq2aAfterTerms: string;
    faq3q: string;
    /** “This page has ” */
    faq3Prefix: string;
    /** Between #pricing and #security link text */
    faq3ConnectorAB: string;
    /** Between security link and privacy link intro */
    faq3Middle: string;
    /** Between privacy and terms links */
    faq3ConnectorCD: string;
    faq3Suffix: string;
    faqTerms: string;
    faqPricing: string;
    faqSecurity: string;
    ctaHeading: string;
    ctaSubIn: string;
    ctaSubOut: string;
    footerTagline: string;
    footerFaq: string;
    footerPrivacy: string;
    footerTerms: string;
    footerContact: string;
    copyright: string;
};

const en: LandingCopy = {
    htmlLang: 'en',
    homeAria: 'QuickMask home',
    navAria: 'Primary',
    navProduct: 'How It Works',
    navPricing: 'Pricing',
    navSecurity: 'Security',
    signIn: 'Sign in',
    signUp: 'Sign up',
    openApp: 'Open app',
    closeMenu: 'Close menu',
    openMenu: 'Open menu',
    langMenuAria: 'Language',
    heroTitleBefore: 'What if redaction took',
    heroTitleHighlight: 'minutes, not hours',
    heroTitleAfterHighlight: '?',
    heroSub1:
        'Describe what to hide in plain language. QuickMask surfaces names, emails, logos, and signatures across your PDFs and images. You review every region before anything is burned in.',
    heroSub2:
        'AI suggestions speed up the work; they do not replace your judgment on legal or high-stakes documents.',
    tryQuickMask: 'Quickly try QuickMask',
    demoVideoAria: 'QuickMask product demo',
    demoLine1: "Show, don't just tell: see sensitive fields disappear in real time.",
    demoLine2:
        'Manual redaction burns hours, and one missed line is enough to break trust. QuickMask narrows the search. You decide what ships.',
    productEyebrow: 'How It Works',
    productHeading: 'From upload to a PDF or image you can share',
    productSub:
        'Three steps. Plain-language control. Suggested regions for the kinds of sensitive content teams redact most often.',
    detectionSr: 'Examples of content QuickMask can help you find:',
    detectionLabels: ['Personal names', 'Email addresses', 'Logos & branding', 'Signatures'],
    steps: [
        {
            title: 'Upload your PDF or image',
            body: 'Drag and drop or browse, up to 50MB, multiple pages. Runs in the browser; nothing to install on your servers.',
        },
        {
            title: 'Describe what to hide',
            body: 'Type what you need in plain language (for example, “Hide all names and email addresses” or “Remove logos and signatures”). QuickMask suggests regions across the document; you are not limited to obvious spots on page one.',
        },
        {
            title: 'Review, then export',
            body: 'Inspect every box, adjust, and iterate. Nothing is permanently redacted until you choose to export a shareable PDF or image you can stand behind.',
        },
    ],
    seeDemo: 'See a full demo',
    pricingHeading: 'Pricing',
    pricingIntro:
        'Register and try QuickMask without a credit card. Plan names and prices appear in the app when you sign in, so you see current limits before you upgrade.',
    pricingFreeBold: 'Free tier:',
    pricingFreeRest:
        'for evaluation. On long documents, automated text detection may only analyze the beginning; caps are shown when you upload or run detection.',
    pricingPaidBold: 'Paid options:',
    pricingPaidRest: 'when you need higher limits or volume. Upgrade paths are inside the product.',
    legalTermsLead: 'Legal terms:',
    pricingBetweenLegalLinks: ', ',
    pricingAfterLegalBlock: '. ',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    moreQuestionsFaq: 'More questions? See',
    createFreeAccount: 'Create free account',
    pricingLegalClosing: '.',
    securityHeading: 'Security & data handling',
    security1Title: '1. Data Privacy',
    security1Body:
        'QuickMask runs on Google Cloud Vertex AI in its enterprise configuration. Under the enterprise service agreement, your data is governed by Google Cloud\u2019s advanced data privacy protections and is never used to train Google\u2019s public foundation models.',
    security2Title: '2. Processing Transparency',
    security2Body:
        'AI-assisted detection runs exclusively within a controlled API environment. We do not use any third-party non-commercial AI subprocessors. All AI inference requests are completed over encrypted channels, keeping your processing private.',
    security3Title: '3. Audit & Control',
    security3Body:
        'The system supports full audit logging of operations. You can permanently delete documents from your workspace at any time. We follow the principle of least privilege: only authorized system processes can reach the processing logic.',
    securityP3Before: 'You can delete documents from your workspace. Retention and account deletion are in the',
    securityP3After: '; acceptable use and limitations are in the',
    securityP4: 'Follow-up questions: see',
    faqHeading: 'FAQ',
    faq1q: 'How fast can we get started?',
    faq1a:
        'Usually within minutes: create an account, upload a PDF or image, and run detection in the browser. There is nothing to install on your own servers.',
    faq2q: 'Will automated detection catch every sensitive item?',
    faq2aBeforeTerms:
        'No. AI suggestions can miss content, over-flag, or misplace regions. QuickMask is meant to speed up review, not replace it. You should manually verify every page before you export or share a document, especially for legal, regulatory, or high-stakes documents. The',
    faq2aAfterTerms: 'describe these limitations.',
    faq3q: 'Where can I read pricing, limits, security, and legal policies?',
    faq3Prefix: 'This page has ',
    faq3ConnectorAB: ' and ',
    faq3Middle: ' summaries. For full legal text, see the ',
    faq3ConnectorCD: ' and ',
    faq3Suffix: '.',
    faqTerms: 'Terms of Service',
    faqPricing: 'Pricing',
    faqSecurity: 'Security & data handling',
    ctaHeading: 'Ready to ship safer documents?',
    ctaSubIn: 'Continue in your workspace.',
    ctaSubOut: 'Start free, no credit card for the trial.',
    footerTagline: 'AI-assisted document redaction',
    footerFaq: 'FAQ',
    footerPrivacy: 'Privacy',
    footerTerms: 'Terms',
    footerContact: 'Contact',
    copyright: 'All rights reserved.',
};

const es: LandingCopy = {
    htmlLang: 'es',
    homeAria: 'Inicio de QuickMask',
    navAria: 'Principal',
    navProduct: 'Cómo funciona',
    navPricing: 'Precios',
    navSecurity: 'Seguridad',
    signIn: 'Iniciar sesión',
    signUp: 'Registrarse',
    openApp: 'Abrir app',
    closeMenu: 'Cerrar menú',
    openMenu: 'Abrir menú',
    langMenuAria: 'Idioma',
    heroTitleBefore: '¿Y si el redactado tomara',
    heroTitleHighlight: 'minutos, no horas',
    heroTitleAfterHighlight: '?',
    heroSub1:
        'Describe qué ocultar en lenguaje claro. QuickMask detecta nombres, correos, logotipos y firmas en tus PDFs e imágenes. Tú revisas cada región antes de que quede aplicado.',
    heroSub2:
        'Las sugerencias de IA agilizan el trabajo; no sustituyen tu criterio en documentos legales o de alto riesgo.',
    tryQuickMask: 'Prueba QuickMask al instante',
    demoVideoAria: 'Demostración del producto QuickMask',
    demoLine1: 'Mejor muéstralo: observa cómo desaparecen datos sensibles en tiempo real.',
    demoLine2:
        'El redactado manual consume horas, y una línea olvidada basta para perder confianza. QuickMask reduce la búsqueda. Tú decides qué se envía.',
    productEyebrow: 'Cómo funciona',
    productHeading: 'De la subida a un PDF o imagen que puedas compartir',
    productSub:
        'Tres pasos. Control en lenguaje natural. Regiones sugeridas para los tipos de contenido sensible que más suelen ocultar los equipos.',
    detectionSr: 'Ejemplos de contenido que QuickMask puede ayudarte a localizar:',
    detectionLabels: ['Nombres', 'Correos electrónicos', 'Logos y marca', 'Firmas'],
    steps: [
        {
            title: 'Sube tu PDF o imagen',
            body: 'Arrastra y suelta o elige archivo, hasta 50 MB y varias páginas. Se ejecuta en el navegador; sin instalar nada en tus servidores.',
        },
        {
            title: 'Describe qué ocultar',
            body: 'Escribe lo que necesitas en lenguaje claro (por ejemplo: «Ocultar todos los nombres y correos» o «Quitar logotipos y firmas»). QuickMask sugiere regiones en todo el documento; no solo en la primera página.',
        },
        {
            title: 'Revisa y exporta',
            body: 'Revisa cada caja, ajusta y repite. Nada queda borrado permanentemente hasta que exportes un PDF o imagen compartible con el que te sientas cómodo.',
        },
    ],
    seeDemo: 'Ver demo completa',
    pricingHeading: 'Precios',
    pricingIntro:
        'Regístrate y prueba QuickMask sin tarjeta de crédito. Los planes y precios aparecen en la app al iniciar sesión, así ves los límites actuales antes de mejorar el plan.',
    pricingFreeBold: 'Plan gratuito:',
    pricingFreeRest:
        'para evaluación. En documentos largos, la detección automática puede analizar solo el inicio; los límites se muestran al subir o ejecutar la detección.',
    pricingPaidBold: 'Opciones de pago:',
    pricingPaidRest: 'cuando necesitas más volumen o límites más altos. Las mejoras están dentro del producto.',
    legalTermsLead: 'Términos legales:',
    pricingBetweenLegalLinks: ', ',
    pricingAfterLegalBlock: '. ',
    privacyPolicy: 'Política de privacidad',
    termsOfService: 'Términos del servicio',
    moreQuestionsFaq: '¿Más preguntas? Ve',
    createFreeAccount: 'Crear cuenta gratuita',
    pricingLegalClosing: '.',
    securityHeading: 'Seguridad y tratamiento de datos',
    security1Title: '1. Privacidad de datos',
    security1Body:
        'QuickMask funciona sobre Google Cloud Vertex AI en su configuración empresarial. Bajo el acuerdo de servicio empresarial, tus datos están regidos por las protecciones avanzadas de privacidad de Google Cloud y nunca se utilizan para entrenar los modelos públicos de Google.',
    security2Title: '2. Transparencia en el procesamiento',
    security2Body:
        'La detección asistida por IA se ejecuta exclusivamente en un entorno de API controlado. No utilizamos ningún subprocesador de IA de terceros no comercial. Todas las solicitudes de inferencia de IA se completan a través de canales cifrados, manteniendo tu procesamiento en privado.',
    security3Title: '3. Auditoría y control',
    security3Body:
        'El sistema soporta registros de auditoría completos de las operaciones. Puedes eliminar permanentemente documentos de tu espacio de trabajo en cualquier momento. Seguimos el principio de mínimo privilegio: solo los procesos del sistema autorizados pueden acceder a la lógica de procesamiento.',
    securityP3Before: 'Puedes borrar documentos de tu espacio. La retención y eliminación de cuenta están en la',
    securityP3After: '; el uso aceptable y las limitaciones están en los',
    securityP4: '¿Seguimiento? Ve',
    faqHeading: 'Preguntas frecuentes',
    faq1q: '¿Qué tan rápido podemos empezar?',
    faq1a:
        'Normalmente en minutos: crea una cuenta, sube un PDF o imagen y ejecuta la detección en el navegador. No hay nada que instalar en tus servidores.',
    faq2q: '¿La detección automática encontrará cada dato sensible?',
    faq2aBeforeTerms:
        'No. Las sugerencias de IA pueden omitir contenido, marcar de más o colocar mal las regiones. QuickMask sirve para acelerar la revisión, no sustituirla. Debes verificar manualmente cada página antes de exportar o compartir un documento, sobre todo en casos legales o delicados. Los',
    faq2aAfterTerms: 'describen estas limitaciones.',
    faq3q: '¿Dónde leo precios, límites, seguridad y políticas legales?',
    faq3Prefix: 'Aquí encontrarás información sobre ',
    faq3ConnectorAB: ' y ',
    faq3Middle: '. Para los textos legales completos, consulta la ',
    faq3ConnectorCD: ' y los ',
    faq3Suffix: '.',
    faqTerms: 'Términos del servicio',
    faqPricing: 'Precios',
    faqSecurity: 'Seguridad y tratamiento de datos',
    ctaHeading: '¿Listo para enviar documentos más seguros?',
    ctaSubIn: 'Continúa en tu espacio de trabajo.',
    ctaSubOut: 'Empieza gratis; la prueba no requiere tarjeta.',
    footerTagline: 'Redacción de documentos asistida por IA',
    footerFaq: 'FAQ',
    footerPrivacy: 'Privacidad',
    footerTerms: 'Términos',
    footerContact: 'Contacto',
    copyright: 'Todos los derechos reservados.',
};

const zh: LandingCopy = {
    htmlLang: 'zh-Hans',
    homeAria: 'QuickMask 首页',
    navAria: '主导航',
    navProduct: '工作原理',
    navPricing: '定价',
    navSecurity: '安全',
    signIn: '登录',
    signUp: '注册',
    openApp: '打开应用',
    closeMenu: '关闭菜单',
    openMenu: '打开菜单',
    langMenuAria: '语言',
    heroTitleBefore: '如果涂黑只需',
    heroTitleHighlight: '几分钟，而不是几小时',
    heroTitleAfterHighlight: '？',
    heroSub1:
        '用白话描述需要隐藏的内容。QuickMask 会在整份 PDF 或图片中提示姓名、邮箱、徽标与签名等区域。导出前由你逐项确认。',
    heroSub2: 'AI 建议可加快进度，但不能替代你对法律或高风险文件的判断。',
    tryQuickMask: '快速试用 QuickMask',
    demoVideoAria: 'QuickMask 产品演示',
    demoLine1: '眼见为实：实时看到敏感字段被处理。',
    demoLine2:
        '手工涂黑耗时长，遗漏一行就足以破坏信任。QuickMask 帮你缩小查找范围；是否对外分享由你决定。',
    productEyebrow: '工作原理',
    productHeading: '从上传到可分享的 PDF 或图片',
    productSub:
        '三步流程。白话控制。针对团队最常遮蔽的敏感内容类型给出区域建议。',
    detectionSr: 'QuickMask 可协助识别的内容示例：',
    detectionLabels: ['姓名', '电子邮件', '标志与品牌', '签名'],
    steps: [
        {
            title: '上传 PDF 或图片',
            body: '拖拽或浏览选择文件，最高 50MB，支持多页。在浏览器中运行，无需在服务器安装任何程序。',
        },
        {
            title: '描述要隐藏的内容',
            body:
                '用白话写出需求（例如「隐藏全部姓名与邮箱」或「去掉标志与签名」）。QuickMask 会在整篇文档上建议区域，不限于第一页的显眼位置。',
        },
        {
            title: '审阅并导出',
            body: '检查每一处框选，调整并重试。只有在你导出可分享的 PDF 或图片时，遮蔽才会定型。',
        },
    ],
    seeDemo: '查看完整演示',
    pricingHeading: '定价',
    pricingIntro:
        '注册即可试用 QuickMask，无需信用卡。登录应用后会显示方案名称与价格，升级前可先了解当前额度。',
    pricingFreeBold: '免费档位：',
    pricingFreeRest: '适用于体验。在长文档上，自动文本检测可能只分析前半部分；上传或运行时界面会提示上限。',
    pricingPaidBold: '付费方案：',
    pricingPaidRest: '需要更高额度或批量时可用。升级入口在产品内。',
    legalTermsLead: '法律条款：',
    pricingBetweenLegalLinks: '与',
    pricingAfterLegalBlock: '。',
    privacyPolicy: '隐私政策',
    termsOfService: '服务条款',
    moreQuestionsFaq: '更多问题请见',
    createFreeAccount: '创建免费账户',
    pricingLegalClosing: '。',
    securityHeading: '安全与数据处理',
    security1Title: '1. 数据隐私',
    security1Body:
        'QuickMask 采用 Google Cloud Vertex AI 企业版引擎。根据企业级服务协议，您的数据受 Google Cloud 高级隐私保护协议约束，且永远不会被用于训练 Google 的公共基础模型。',
    security2Title: '2. 处理透明度',
    security2Body:
        'AI 辅助检测仅在受控的 API 环境下执行。我们不使用任何第三方非商业级 AI 子处理器。所有 AI 推理请求均在加密通道内完成，确保处理过程的私密性。',
    security3Title: '3. 审计与控制',
    security3Body:
        '系统支持完整的操作日志审计。您可以随时在工作区中永久删除文档。我们遵循"最小权限原则"，确保只有经过授权的系统进程能触达处理逻辑。',
    securityP3Before: '你可以从工作区删除文档。数据留存与注销账户请参阅',
    securityP3After: '；合理使用与限制见',
    securityP4: '延伸阅读：请参阅',
    faqHeading: '常见问题',
    faq1q: '多快可以上手？',
    faq1a:
        '通常几分钟即可：创建账户、上传 PDF 或图片，并在浏览器中运行检测；无需在您自有服务器安装任何组件。',
    faq2q: '自动检测能覆盖所有敏感项吗？',
    faq2aBeforeTerms:
        '不能。AI 提示可能遗漏、误报或偏移区域。QuickMask 旨在加快人工审核，而非替代审核。导出或对外分享文件前，尤其是对法律或高风险文件，请务必逐页人工核对。',
    faq2aAfterTerms: '说明了这些限制。',
    faq3q: '在哪里查看定价、额度、安全和法律政策？',
    faq3Prefix: '本页提供',
    faq3ConnectorAB: '与',
    faq3Middle: '的摘要。完整法律文本请参阅',
    faq3ConnectorCD: '与',
    faq3Suffix: '。',
    faqTerms: '服务条款',
    faqPricing: '定价',
    faqSecurity: '安全与数据处理',
    ctaHeading: '准备好发送更安全的文件了吗？',
    ctaSubIn: '回到你的工作区继续。',
    ctaSubOut: '免费开始试用，试用无需信用卡。',
    footerTagline: 'AI 辅助文件遮蔽',
    footerFaq: '常见问题',
    footerPrivacy: '隐私',
    footerTerms: '条款',
    footerContact: '联系',
    copyright: '保留所有权利。',
};

const byLang: Record<LandingLang, LandingCopy> = { en, es, zh };

export function getLandingCopy(lang: LandingLang): LandingCopy {
    return byLang[lang];
}
